import { Directive, ElementRef, Input, OnChanges, SimpleChanges, forwardRef, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface TextMaskConfig {
    mask: (string | RegExp)[] | false;
    guide?: boolean;
    pipe?: (conformedValue: string) => string;
    modelClean?: boolean;
}

export function conformToMask(
    rawValue: string,
    mask: (string | RegExp)[],
    config: { guide?: boolean } = {}
): { conformedValue: string } {
    const guide = config.guide !== false;
    let conformedValue = '';
    let rawIndex = 0;

    for (let i = 0; i < mask.length && rawIndex < rawValue.length; i++) {
        const maskChar = mask[i];
        if (maskChar instanceof RegExp) {
            while (rawIndex < rawValue.length) {
                const rawChar = rawValue[rawIndex];
                rawIndex++;
                if (maskChar.test(rawChar)) {
                    conformedValue += rawChar;
                    break;
                }
            }
        } else {
            conformedValue += maskChar;
            if (rawValue[rawIndex] === maskChar) {
                rawIndex++;
            }
        }
    }

    return { conformedValue };
}

@Directive({
    standalone: false,
    selector: '[textMask]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextMaskDirective),
            multi: true,
        },
    ],
})
export class TextMaskDirective implements ControlValueAccessor, OnChanges {
    @Input('textMask') textMaskConfig: TextMaskConfig = { mask: [] };

    private inputElement: HTMLInputElement;
    private onChange = (_: any) => {};
    private onTouched = () => {};

    constructor(private el: ElementRef, private renderer: Renderer2) {
        this.inputElement = this.el.nativeElement;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['textMaskConfig']) {
            this.updateValue(this.inputElement.value);
        }
    }

    writeValue(value: any): void {
        const displayValue = this.applyMask(value || '');
        this.renderer.setProperty(this.inputElement, 'value', displayValue);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
        this.inputElement.addEventListener('input', (e: Event) => {
            const target = e.target as HTMLInputElement;
            this.updateValue(target.value);
        });
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
        this.inputElement.addEventListener('blur', () => this.onTouched());
    }

    setDisabledState(isDisabled: boolean): void {
        this.renderer.setProperty(this.inputElement, 'disabled', isDisabled);
    }

    private updateValue(rawValue: string): void {
        const maskedValue = this.applyMask(rawValue);
        this.renderer.setProperty(this.inputElement, 'value', maskedValue);
        const modelValue = this.textMaskConfig.modelClean
            ? this.stripMaskLiterals(maskedValue)
            : maskedValue;
        this.onChange(modelValue);
    }

    private applyMask(value: string): string {
        if (!this.textMaskConfig || !this.textMaskConfig.mask || this.textMaskConfig.mask === false as any) {
            return value;
        }
        const result = conformToMask(value, this.textMaskConfig.mask, {
            guide: this.textMaskConfig.guide,
        });
        let conformed = result.conformedValue;
        if (this.textMaskConfig.pipe) {
            conformed = this.textMaskConfig.pipe(conformed);
        }
        return conformed;
    }

    private stripMaskLiterals(value: string): string {
        if (!this.textMaskConfig || !this.textMaskConfig.mask || this.textMaskConfig.mask === false as any) {
            return value;
        }
        let result = '';
        const mask = this.textMaskConfig.mask;
        for (let i = 0; i < value.length && i < mask.length; i++) {
            if (mask[i] instanceof RegExp) {
                result += value[i];
            }
        }
        return result;
    }
}
